/**
 * Derived peer UI state — single source of truth for how a peer's connection
 * is surfaced to the user.
 *
 * Why this exists:
 *   RTCPeerConnectionState / RTCIceConnectionState are *machine-local* WebRTC
 *   states. They have ZERO knowledge of signal-server presence (mongo
 *   heartbeat). A freshly-created RTCPeerConnection is always
 *   `state === 'new'`, regardless of whether:
 *     - we have never attempted a connection (initializing)
 *     - we attempted and got HTTP 202 (peer offline per mongo presence)
 *     - we are actively signaling offers (truly connecting)
 *     - we went dormant after exhausting retry backoff
 *
 *   Surfacing the raw RTC state in the UI conflates all four into
 *   "Connecting..." or "State: new", which is actively misleading.
 *
 *   `derivePeerUIState()` fuses the RTC state with the authoritative presence
 *   signals (`MobyNode.offlinePeers`, `peer.retryPhase`, retry attempt count)
 *   into a single logical state that maps cleanly to a label and color.
 *
 * See docs/connection-desync-audit.md — P0-C.
 */

export type RetryPhase = 'aggressive' | 'moderate' | 'background' | 'dormant';

export type PeerUIState =
  | 'initializing'      // page just loaded, no attempts yet
  | 'offline'           // mongo presence says offline (signal-server 202)
  | 'dormant'           // gave up active retries, event-driven only
  | 'connecting'        // first-time or post-offline signaling in progress
  | 'reconnecting'      // previously connected, now cycling (ICE restart)
  | 'connected-direct'  // ICE + channels up, selected pair is host/srflx
  | 'connected-relay'   // ICE + channels up, selected pair uses TURN
  | 'connected'         // ICE + channels up, pair type unknown (pre-stats)
  | 'degraded'          // transient: was connected, ICE disconnected
  | 'failed'            // terminal ICE/DTLS failure
  | 'closed';           // explicit teardown

export interface PeerStateInputs {
  rtcState?: RTCPeerConnectionState;
  iceState?: RTCIceConnectionState;
  /** true if MobyNode.offlinePeers.has(address) */
  isOfflineInPresence: boolean;
  retryPhase?: RetryPhase;
  reconnectionAttempts?: number;
  /** chat data channel open — proxy for "fully usable connection" */
  chatChannelOpen?: boolean;
  /** derived from getStats() — may be 'unknown' when not yet measured */
  connectionType?: 'direct' | 'relay' | 'unknown';
}

/**
 * Pure state-machine derivation. No side effects. No React dependency.
 *
 * Order of evaluation is deliberate: terminal states first, then presence
 * (which trumps RTC state when we're not actively connected), then RTC
 * progression, falling through to initialization by default.
 */
export function derivePeerUIState(inputs: PeerStateInputs): PeerUIState {
  const {
    rtcState,
    iceState,
    isOfflineInPresence,
    retryPhase,
    reconnectionAttempts = 0,
    chatChannelOpen = false,
    connectionType,
  } = inputs;

  // Terminal states first — these are authoritative regardless of presence.
  if (rtcState === 'closed') return 'closed';
  if (rtcState === 'failed' || iceState === 'failed') return 'failed';

  // Fully-connected takes priority over presence: if WebRTC says we're up
  // and channels are open, we ARE connected even if a stale offlinePeers
  // flag hasn't cleared yet.
  if (
    rtcState === 'connected' &&
    (iceState === 'connected' || iceState === 'completed') &&
    chatChannelOpen
  ) {
    if (connectionType === 'direct') return 'connected-direct';
    if (connectionType === 'relay') return 'connected-relay';
    return 'connected';
  }

  // Presence truth: signal server has confirmed this peer is offline.
  // This overrides any transient RTC state (e.g. a stale 'new' on a peer
  // whose connection was never going to succeed this cycle).
  if (isOfflineInPresence) {
    return retryPhase === 'dormant' ? 'dormant' : 'offline';
  }

  // Dormant is also a meaningful state independent of presence — the retry
  // loop gave up on this peer and is waiting for an event-driven trigger.
  if (retryPhase === 'dormant') return 'dormant';

  // Degraded: was connected, now recovering. ICE disconnected is the W3C
  // transient indicator; rtcState may also surface 'disconnected'.
  if (iceState === 'disconnected' || rtcState === 'disconnected') {
    return 'degraded';
  }

  // Actively signaling.
  if (
    rtcState === 'connecting' ||
    iceState === 'checking' ||
    (rtcState === 'connected' && !chatChannelOpen) // RTC up, channels still opening
  ) {
    return reconnectionAttempts > 0 ? 'reconnecting' : 'connecting';
  }

  // Fresh / between-attempts.
  if (rtcState === 'new' || iceState === 'new' || !rtcState) {
    return reconnectionAttempts > 0 ? 'reconnecting' : 'initializing';
  }

  return 'initializing';
}

// ─── Lifecycle state machine (7 states + orthogonal modifiers) ───────────────
//
// PR-1 of docs/connection-state-machine.md. The 11-state `PeerUIState` above
// was a flat enum that conflated lifecycle (idle/connecting/connected/…) with
// modifiers (direct vs relay). The new shape separates those concerns so
// downstream features (groups star meshing, multisource chunk routing, AV
// topology) can branch on lifecycle without caring about relay/busy, and the
// UI can compose the "8 looks" (connected+relay, connected+busy, …) without
// state explosion.
//
// This layer is PURELY a projection of the existing 11-state classifier plus
// a few authoritative overrides. See `derivePeerLifecycleState` below.

/** 7 canonical lifecycle states — one per observable transport phase. */
export type PeerLifecycleState =
  | 'idle'          // page just loaded, no attempts yet (or post-teardown)
  | 'connecting'    // first-time signaling in progress
  | 'connected'     // RTC + channels up, peer reachable
  | 'degraded'      // app-level probe flagging (PR-4); not used in PR-1
  | 'reconnecting'  // previously connected or retrying; cycling ICE
  | 'disconnected'  // peer unreachable per presence OR retry exhausted
  | 'failed';       // terminal — user tombstoned contact, or RTC closed

/**
 * Orthogonal modifiers — flat flags that compose with any lifecycle state
 * without forcing a 7 × 2⁴ state explosion. PR-1 populates `relay` from
 * connection-type; the remaining flags are scaffolded for PR-3 (`localBusy`,
 * `remoteBusy`, `throttled`) and PR-3+ (`relayPath` for multi-hop).
 */
export interface PeerModifiers {
  /** true iff the selected candidate pair uses a relay (TURN or group star). */
  relay: boolean;
  /** Chain of relay peer IDs. v1 populates at most one entry; multi-hop deferred. */
  relayPath: string[];
  /** true while this node is mid-transfer with the peer (suppresses heartbeat). */
  localBusy: boolean;
  /** true when the peer has signaled BUSY_SIGNAL to us (PR-3). */
  remoteBusy: boolean;
  /** true under OPFS-critical / memory pressure (PR-3). */
  throttled: boolean;
}

export interface PeerStateSnapshot {
  peerId: string;
  lifecycle: PeerLifecycleState;
  modifiers: PeerModifiers;
  /** Wall-clock timestamp (ms) when lifecycle last changed. */
  lastTransitionAt: number;
  /** Human-readable reason string, for diagnostics export. */
  lastTransitionReason: string;
  /** The underlying 11-state UI enum value. Retained for PR-2 migration. */
  uiStateRaw: PeerUIState;
}

export interface PeerLifecycleInputs extends PeerStateInputs {
  /** Contact tombstone flag — user deleted this contact, force `failed`. */
  isContactDeleted?: boolean;
  /** Peer was explicitly closed via peer.close() — force `failed` as safety net. */
  peerClosed?: boolean;
  /**
   * Previous lifecycle state, if any. Used to disambiguate `degraded` UI:
   * a 2s ICE_GRACE blip from `connected` is a no-op (stay `connected`),
   * but a `degraded` projection from `connecting` MUST stay at `connecting`
   * (we never had a working channel). See the audit in
   * docs/connection-state-machine.md (PR-2 grace-blip fix).
   */
  previousLifecycle?: PeerLifecycleState;
  /**
   * Count of in-flight file transfers with this peer. Drives the
   * `localBusy` modifier — non-zero means we're actively transferring
   * with the peer and any heartbeat-driven reconnect noise should be
   * treated as expected, not a desync.
   */
  activeTransferCount?: number;
  /**
   * Node-level throttle flag — true when the local node is under OPFS or
   * memory pressure. Fans out to every peer's `throttled` modifier; the
   * pressure isn't peer-specific. Sourced from
   * `extensionCapabilityService.lastPressureState !== 'normal'`.
   */
  isThrottled?: boolean;
}

const EMPTY_MODIFIERS: PeerModifiers = {
  relay: false,
  relayPath: [],
  localBusy: false,
  remoteBusy: false,
  throttled: false,
};

/**
 * Project inputs onto the 7-state lifecycle + modifier shape.
 *
 * Classifier contract (see docs/connection-state-machine.md Q9.A, PR-2):
 *   - `isContactDeleted` / `peerClosed` override everything to `failed`.
 *   - `initializing`    → `idle`
 *   - `connecting`      → `connecting`
 *   - `reconnecting`    → `reconnecting`
 *   - `connected*`      → `connected` (with `relay` modifier from connectionType)
 *   - `degraded`        → grace-blip rule: stay at `previousLifecycle` UNLESS
 *                          the peer was previously `connected` or `degraded`,
 *                          in which case collapse to `connected` (the original
 *                          2s ICE_GRACE smoothing). Without the gating, a
 *                          `connecting` peer whose ICE briefly hits
 *                          `disconnected` was wrongly promoted to `connected`
 *                          (see audit: docs trace at line 5734 of K Linux log,
 *                          chat channel never opened).
 *   - `offline` / `dormant` → `disconnected`
 *   - `failed` / `closed`   → `failed`
 *
 * Modifiers (orthogonal flags, populated regardless of lifecycle):
 *   - `relay`     ← `connectionType === 'relay'` OR `uiStateRaw` is `connected-relay`
 *   - `localBusy` ← `(activeTransferCount ?? 0) > 0`
 *   - `throttled` ← `isThrottled`
 *   - `remoteBusy`/`relayPath` — scaffolded, populated by PR-3+
 *
 * The `reason` string distinguishes semantically-equivalent mappings for
 * diagnostics (e.g. "retry-exhausted" vs "signal-server-offline" both map to
 * `disconnected`).
 */
export function derivePeerLifecycleState(
  inputs: PeerLifecycleInputs,
): {
  lifecycle: PeerLifecycleState;
  modifiers: PeerModifiers;
  reason: string;
  uiStateRaw: PeerUIState;
} {
  const { isContactDeleted, peerClosed, previousLifecycle } = inputs;

  // Authoritative overrides trump RTC state — the transport may still be
  // in-flight when the user deletes a contact, but the lifecycle is done.
  if (isContactDeleted) {
    return {
      lifecycle: 'failed',
      modifiers: { ...EMPTY_MODIFIERS },
      reason: 'contact-deleted',
      uiStateRaw: 'failed',
    };
  }
  if (peerClosed) {
    return {
      lifecycle: 'failed',
      modifiers: { ...EMPTY_MODIFIERS },
      reason: 'peer-closed',
      uiStateRaw: 'closed',
    };
  }

  const uiStateRaw = derivePeerUIState(inputs);
  const modifiers = buildModifiers(inputs, uiStateRaw);

  switch (uiStateRaw) {
    case 'initializing':
      return { lifecycle: 'idle', modifiers, reason: 'initializing', uiStateRaw };

    case 'connecting':
      return { lifecycle: 'connecting', modifiers, reason: 'rtc-connecting', uiStateRaw };

    case 'reconnecting':
      return { lifecycle: 'reconnecting', modifiers, reason: 'rtc-reconnecting', uiStateRaw };

    case 'connected-direct':
      return {
        lifecycle: 'connected',
        modifiers,
        reason: 'ice-connected-direct',
        uiStateRaw,
      };

    case 'connected-relay':
      // relayPath population deferred to PR-3+ (multi-hop shape stub).
      return {
        lifecycle: 'connected',
        modifiers,
        reason: 'ice-connected-relay',
        uiStateRaw,
      };

    case 'connected':
      // Pre-stats connected. Relay flag is `false` until getStats() resolves
      // the candidate pair type via `connectionType` input.
      return { lifecycle: 'connected', modifiers, reason: 'ice-connected', uiStateRaw };

    case 'degraded':
      // Grace-blip rule (PR-2 fix): the 2s ICE_GRACE smoothing only applies
      // when we WERE connected. From any other prior lifecycle, the peer's
      // chat channel is not actually open and projecting `connected` is a
      // lie — stick to the previous lifecycle.
      if (previousLifecycle === 'connected' || previousLifecycle === 'degraded') {
        return {
          lifecycle: 'connected',
          modifiers,
          reason: 'ice-grace-blip-treated-as-connected',
          uiStateRaw,
        };
      }
      // No prior lifecycle (first observation) or a non-connected prior:
      // hold at the prior lifecycle, defaulting to `connecting` for the
      // initial-classification edge case where we observe `iceState ===
      // disconnected` before ever reaching `connected`.
      return {
        lifecycle: previousLifecycle ?? 'connecting',
        modifiers,
        reason: previousLifecycle
          ? `ice-grace-blip-held-at-${previousLifecycle}`
          : 'ice-grace-blip-no-prior-state',
        uiStateRaw,
      };

    case 'offline':
      return {
        lifecycle: 'disconnected',
        modifiers,
        reason: 'signal-server-offline',
        uiStateRaw,
      };

    case 'dormant':
      return {
        lifecycle: 'disconnected',
        modifiers,
        reason: 'retry-exhausted',
        uiStateRaw,
      };

    case 'failed':
      return { lifecycle: 'failed', modifiers, reason: 'rtc-failed', uiStateRaw };

    case 'closed':
      return { lifecycle: 'failed', modifiers, reason: 'rtc-closed', uiStateRaw };
  }
}

/**
 * Compute the orthogonal modifier flags from inputs + raw UI state.
 *
 * Modifier rules (PR-2):
 *   - `relay`       ← raw UI state is `connected-relay` OR connectionType === 'relay'
 *   - `localBusy`   ← any active transfer with this peer
 *   - `throttled`   ← node-level memory/OPFS pressure
 *   - `remoteBusy`  ← BUSY_SIGNAL not yet wired (PR-3)
 *   - `relayPath`   ← multi-hop not yet wired (PR-3+)
 *
 * Modifiers are applied regardless of lifecycle — a `disconnected` peer
 * can still be `localBusy` if a queued transfer is sitting in the pipeline,
 * and a `connecting` peer can still be `throttled` while the node is under
 * pressure.
 */
function buildModifiers(
  inputs: PeerLifecycleInputs,
  uiStateRaw: PeerUIState,
): PeerModifiers {
  const isRelay =
    uiStateRaw === 'connected-relay' || inputs.connectionType === 'relay';
  return {
    relay: isRelay,
    relayPath: [],
    localBusy: (inputs.activeTransferCount ?? 0) > 0,
    remoteBusy: false,
    throttled: inputs.isThrottled ?? false,
  };
}

// ─── UI descriptors ──────────────────────────────────────────────────────────

export type PeerUITone = 'green' | 'blue' | 'amber' | 'red' | 'gray' | 'dimmed';

export interface PeerUIDescriptor {
  /** short label for compact UI (e.g. chat list dot) */
  label: string;
  /** full sentence for tooltip */
  tooltip: string;
  /** hex color for the dot / badge */
  color: string;
  tone: PeerUITone;
  /** whether the icon should animate-spin */
  spin: boolean;
  /**
   * Which Lucide icon to render in the diagnostic badge. The consumer maps
   * this key to an icon component — keeps this module free of React imports.
   */
  iconKey:
    | 'zap'
    | 'refresh'
    | 'signal'
    | 'signalLow'
    | 'signalMedium'
    | 'signalHigh'
    | 'alert'
    | 'moon'
    | 'dot';
}

export interface DescriptorContext {
  rtt?: number;
  /**
   * Smoothed jitter in milliseconds (variance of recent RTT samples,
   * exponentially smoothed 80/20 and capped at 50% of median RTT). Surfaces
   * in the `connected-direct` tooltip alongside RTT so a user diagnosing
   * "why is my AV laggy?" or "why did this peer drop a tier?" can see the
   * variance directly. Computed in the same `getStats()` poll that produces
   * RTT — no extra CPU cost. See provider.tsx:7413-7461 for the smoothing
   * pipeline.
   */
  jitter?: number;
  reconnectionAttempts?: number;
  retryPhase?: RetryPhase;
  errorCount?: number;
  healthScore?: 'green' | 'yellow' | 'red' | 'gray';
  /**
   * `localBusy` modifier from the lifecycle store. When false, a connected
   * peer renders with a dimmer "idle" tone so the contact list visually
   * distinguishes peers that are mid-transfer from peers that are merely
   * reachable. Defaults to `false` (idle).
   *
   * Why this lives in DescriptorContext instead of being a new lifecycle
   * state: the lifecycle state machine intentionally separates "is the
   * transport up" (a 7-state lifecycle) from "what's the peer doing right
   * now" (orthogonal modifiers). Adding `connected-busy` and
   * `connected-idle` as separate states would re-conflate them. The
   * descriptor's job is to render the composition.
   */
  localBusy?: boolean;
}

// Central color palette — matches the hex values that already existed in
// contacts-drawer.tsx / chat-list-panel.tsx / chat-drawer.tsx so no visual
// regression for the states that were already rendered correctly.
//
// `idleGreen` is a desaturated variant of the connected green — used when a
// peer is connected but not transferring (`localBusy === false`). The color
// must still read as "good / online" at a glance; this is just a tonal step
// down so an idle peer doesn't compete visually with a peer that's actively
// pushing chunks.
const COLORS = {
  green: '#4CAF50',
  idleGreen: '#7CB07F',
  yellow: '#FFC107',
  blue: '#2196F3',
  amber: '#FF9800',
  red: '#F44336',
  gray: '#9E9E9E',
  dimmed: '#616161',
} as const;

export function getPeerUIDescriptor(
  state: PeerUIState,
  ctx: DescriptorContext = {},
): PeerUIDescriptor {
  switch (state) {
    case 'initializing':
      return {
        label: 'Initializing',
        tooltip: 'Initializing…',
        color: COLORS.gray,
        tone: 'gray',
        spin: false,
        iconKey: 'dot',
      };

    case 'offline':
      return {
        label: 'Offline',
        tooltip: 'Offline — peer is not reachable via the signal server',
        color: COLORS.gray,
        tone: 'gray',
        spin: false,
        iconKey: 'signalLow',
      };

    case 'dormant':
      return {
        label: 'Dormant',
        tooltip: 'Dormant — waiting for peer to come back online',
        color: COLORS.dimmed,
        tone: 'dimmed',
        spin: false,
        iconKey: 'moon',
      };

    case 'connecting':
      return {
        label: 'Connecting',
        tooltip: 'Connecting…',
        color: COLORS.blue,
        tone: 'blue',
        spin: true,
        iconKey: 'refresh',
      };

    case 'reconnecting': {
      const phase = ctx.retryPhase;
      const attempt = ctx.reconnectionAttempts ?? 0;
      const phaseLabel = phase ? ` (${phase} phase` : '';
      const attemptLabel = attempt > 0 ? `, attempt ${attempt}` : '';
      const suffix = phase ? `${phaseLabel}${attemptLabel})` : '';
      // Map retry phase to an intensity icon — matches legacy Signal* icons.
      const iconKey: PeerUIDescriptor['iconKey'] =
        phase === 'background' ? 'signalLow'
        : phase === 'moderate' ? 'signalMedium'
        : 'signalHigh';
      return {
        label: 'Reconnecting',
        tooltip: `Reconnecting${suffix}`,
        color: phase === 'background' ? COLORS.amber : COLORS.yellow,
        tone: 'amber',
        spin: false,
        iconKey,
      };
    }

    case 'connected-direct': {
      const rtt = ctx.rtt != null ? `${ctx.rtt} ms` : 'N/A';
      // Jitter is appended only when measured. A peer that has measured RTT
      // but no jitter sample yet (first 3 RTT samples — see provider.tsx:7456)
      // gets a tooltip without the jitter clause rather than "jitter N/A"
      // noise. The smoothing pipeline guarantees jitter > 0 once produced,
      // so consumers should pass `null`/`undefined` (not `0`) for "no data".
      // Round to 1 decimal: the smoothing pipeline produces floats like
      // 4.766666666666667 — `.toFixed(1)` keeps the resolution useful for
      // diagnosing AV laggy/tier-drop without dumping JS float noise.
      const jitterClause = ctx.jitter != null ? `, jitter ${ctx.jitter.toFixed(1)} ms` : '';
      const health = ctx.healthScore ?? 'green';
      // Healthy connected peers desaturate while idle (`localBusy === false`)
      // so a chat list with 20 reachable contacts doesn't feel like 20
      // simultaneous active transfers. Yellow / red health overrides idle —
      // a degrading peer should still grab attention regardless of activity.
      const baseGreen = ctx.localBusy ? COLORS.green : COLORS.idleGreen;
      const color =
        health === 'yellow' ? COLORS.yellow
        : health === 'red' ? COLORS.red
        : baseGreen;
      const idleSuffix = !ctx.localBusy && health === 'green' ? ' (idle)' : '';
      return {
        label: 'Direct',
        tooltip: `Direct P2P — RTT ${rtt}${jitterClause}${idleSuffix}`,
        color,
        tone: health === 'red' ? 'red' : health === 'yellow' ? 'amber' : 'green',
        spin: false,
        iconKey: 'zap',
      };
    }

    case 'connected-relay':
      return {
        label: 'Relay',
        tooltip: ctx.localBusy
          ? 'Connected via relay (TURN or group star topology)'
          : 'Connected via relay (idle)',
        color: COLORS.blue,
        tone: 'blue',
        spin: false,
        iconKey: 'refresh',
      };

    case 'connected': {
      const health = ctx.healthScore;
      const baseGreen = ctx.localBusy ? COLORS.green : COLORS.idleGreen;
      const color =
        health === 'yellow' ? COLORS.yellow
        : health === 'red' ? COLORS.red
        : baseGreen;
      const tooltip = ctx.localBusy ? 'Connected' : 'Connected (idle)';
      return {
        label: 'Connected',
        tooltip,
        color,
        tone: health === 'red' ? 'red' : health === 'yellow' ? 'amber' : 'green',
        spin: false,
        iconKey: 'signal',
      };
    }

    case 'degraded':
      return {
        label: 'Degraded',
        tooltip: 'Connection degraded — recovering',
        color: COLORS.amber,
        tone: 'amber',
        spin: false,
        iconKey: 'signalMedium',
      };

    case 'failed': {
      const errs = ctx.errorCount ?? 0;
      return {
        label: 'Failed',
        tooltip: `Connection failed${errs > 0 ? ` (${errs} errors)` : ''}`,
        color: COLORS.red,
        tone: 'red',
        spin: false,
        iconKey: 'alert',
      };
    }

    case 'closed':
      return {
        label: 'Closed',
        tooltip: 'Connection closed',
        color: COLORS.gray,
        tone: 'gray',
        spin: false,
        iconKey: 'dot',
      };
  }
}

// ─── Silent channel-close detection ──────────────────────────────────────────

/**
 * Inputs to the silent-close detector.
 *
 * Captured at the moment a chat/file/av channel close event fires.
 */
export interface SilentCloseInputs {
  /** True if MobyPeer.close() has been called (intentional teardown). */
  closed: boolean;
  /** True if a previous channel-close on this peer already triggered detection. */
  alreadyDetected: boolean;
  /** RTCPeerConnection.connectionState read at the moment the channel closed. */
  rtcState: RTCPeerConnectionState;
}

/**
 * Decide whether a channel-close event represents a *silent* close that the
 * existing connection-state-change handlers will NOT recover from.
 *
 * The pathological case (logout/login blue-stuck bug, stage 3): a stale offer
 * round-trip caused by a zombie peer triggers the remote side to tear down
 * its RTCPeerConnection. Our chat/file/av channels close as a side-effect,
 * but our local RTCPeerConnection.connectionState stays at `'connected'`
 * indefinitely — neither the peerConnectionLoop nor the
 * onconnectionstatechange handler ever fires recovery.
 *
 * Detection rules:
 *   1. If `closed` (intentional teardown) — never a silent close.
 *   2. If `alreadyDetected` (another channel on the same peer already fired)
 *      — already handled, dedupe.
 *   3. If `rtcState !== 'connected'` — RTC has already transitioned to a
 *      degraded state; the existing recovery path owns this peer.
 *
 * Otherwise: a silent close. Caller should schedule a brief grace timer and,
 * if RTC has not caught up by then, emit `connection-dead` to trigger peer
 * replacement.
 */
export function shouldDetectSilentClose(inputs: SilentCloseInputs): boolean {
  if (inputs.closed) return false;
  if (inputs.alreadyDetected) return false;
  if (inputs.rtcState !== 'connected') return false;
  return true;
}

// ─── Connection-state write dedup (P3) ──────────────────────────────────────

/**
 * The four RTC state fields that get persisted to `contact.connection`
 * via `MobyPeer._flushConnectionStateWrite`. Captured here as a typed
 * shape so the dedup signature builder is fully testable and uncoupled
 * from the live `RTCPeerConnection` instance.
 */
export interface ConnectionStateSnapshot {
  /** `RTCPeerConnection.connectionState` */
  state: string;
  /** `RTCPeerConnection.signalingState` */
  signalingState: string;
  /** `RTCPeerConnection.iceConnectionState` */
  iceState: string;
  /** `RTCPeerConnection.iceGatheringState` */
  iceGatheringState: string;
}

/**
 * Build a stable signature string from a connection state snapshot.
 *
 * Used as the dedup key in `MobyPeer._flushConnectionStateWrite` (P3 fix
 * for `docs/connection-desync-audit.md` "Decouple connection-state-change
 * from Dexie writes"). Every `db.contacts.modify()` call kicks the
 * `useSuspenseLiveQuery` of contacts in `chat-list-panel`; during a
 * reconnect storm the four RTC handlers fire 6-10 times per peer per
 * blip cycle, but the resulting four-field tuple often cycles through a
 * handful of repeating values. Skipping the write when the signature
 * matches the last-written one cuts the chat-list re-render rate
 * substantially without losing UX (the signature changes whenever any
 * state field genuinely changes).
 *
 * The signature is a `|`-joined string rather than a deep-equal compare
 * so the hot-path cost is a single string concat + Map lookup. The four
 * values have small fixed alphabets (`new|connecting|connected|...`) so
 * collision is structurally impossible.
 *
 * Note: the persisted `lastUpdated` timestamp is intentionally NOT part
 * of the signature — including it would defeat dedup on every call.
 * Time-based freshness is recovered through the unconditional flush on
 * terminal lifecycle events.
 */
export function buildConnectionStateSignature(
  snapshot: ConnectionStateSnapshot,
): string {
  return `${snapshot.state}|${snapshot.signalingState}|${snapshot.iceState}|${snapshot.iceGatheringState}`;
}

// ─── Retry-loop kick guard (P1-B) ────────────────────────────────────────────

/**
 * Inputs for deciding whether to kick the retry loop from an external trigger
 * (e.g. HELLO handler).
 *
 * These are the peer state fields that determine if it's safe to initiate
 * a new negotiation attempt without racing with the ICE-driven retry logic.
 */
export interface RetryLoopKickInputs {
  /** Timestamp (ms) of the last successful kick. 0 if never kicked. */
  lastKickAt: number;
  /** Minimum ms between kicks — rate-limits HELLO handler thrashing. */
  kickCooldownMs: number;
  /** True if ICE-driven retry is already in progress (isRetryInProgress flag). */
  isRetryInProgress: boolean;
  /** True if the peer is ready for negotiation (isReady flag). */
  isReady: boolean;
  /** True if the peer has been explicitly closed. */
  closed: boolean;
  /** Current RTCPeerConnection.signalingState. */
  signalingState: string;
  /** Current RTCPeerConnection.connectionState. */
  connectionState: string;
}

/**
 * Decide whether an external trigger (e.g. HELLO handler) should kick the
 * retry loop by calling `peer.triggerNegotiationIfNeeded()`.
 *
 * Guards (in order of evaluation):
 *   1. **Rate-limit**: Enforce minimum cooldown between kicks to prevent
 *      HELLO handler thrashing when multiple peers come online together.
 *   2. **Retry-in-progress**: If ICE-driven retry is already active
 *      (`isRetryInProgress`), don't interfere — the retry loop owns this peer.
 *   3. **Readiness**: Peer must be `isReady` and not `closed`.
 *   4. **Signaling state**: Only kick when signaling is `'stable'` — kicking
 *      during offer/answer exchange creates competing offers.
 *   5. **Connection state**: Skip if already `'connected'` — healthy connections
 *      don't need external kicks.
 *
 * This is the P1-B fix for the competing-offer race in the HELLO handler.
 * The two fallback `triggerNegotiationIfNeeded()` calls (lines 31192 and 31706
 * in provider.tsx) are replaced with `peer.kickRetryLoop()`, which first
 * calls this helper before triggering negotiation.
 */
export function shouldKickRetryLoop(inputs: RetryLoopKickInputs): boolean {
  const {
    lastKickAt,
    kickCooldownMs,
    isRetryInProgress,
    isReady,
    closed,
    signalingState,
    connectionState,
  } = inputs;

  // Rate-limit: prevent HELLO handler thrashing
  const now = Date.now();
  if (lastKickAt > 0 && now - lastKickAt < kickCooldownMs) {
    return false;
  }

  // Don't race with ICE-driven retry
  if (isRetryInProgress) {
    return false;
  }

  // Peer must be ready and not closed
  if (!isReady || closed) {
    return false;
  }

  // Only kick when signaling is stable — otherwise we create competing offers
  if (signalingState !== 'stable') {
    return false;
  }

  // Skip healthy connections
  if (connectionState === 'connected') {
    return false;
  }

  return true;
}
